import configJSON from 'config.json';
import type { TenantId } from 'tenants/types/tenant';

export const isServerSide = () => (typeof window === 'undefined');

export const isClientSide = () => (typeof window !== 'undefined');

// Lazy import to avoid circular dependency - only loaded when needed
let getTenantByIdCache: ((id: TenantId) => { hosts?: string[] } | undefined) | null = null;

function getTenantByIdLazy(tenantId: TenantId): { hosts?: string[] } | undefined {
  if (!getTenantByIdCache) {
    // Use require() to avoid circular dependency - only called server-side
    // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
    getTenantByIdCache = require('tenants/manager').getTenantById;
  }
  return getTenantByIdCache(tenantId);
}

/**
 * Gets the origin (protocol + host) for URL construction.
 * 
 * On the client side, uses the actual window.location.origin.
 * On the server side, uses the tenant's primary host if tenantId is provided,
 * otherwise falls back to a default origin. The origin is only used for URL
 * parsing in getTargetedUrl - the final output is pathname + search, so the
 * specific origin value doesn't affect the result.
 * 
 * @param tenantId - Optional tenant ID to use tenant's host configuration (server-side)
 * @returns The origin URL (e.g., 'https://www.zoocasa.com' or window.location.origin on client)
 */
export function getOrigin(tenantId?: TenantId): string {
  // Client-side: use actual window.location.origin
  if (isClientSide() && typeof window !== 'undefined' && window.location) {
    return window.location.origin;
  }

  // Server-side: use tenant's primary host if tenantId is provided
  // Use lazy import to avoid circular dependency issues
  if (tenantId) {
    const tenant = getTenantByIdLazy(tenantId);
    if (tenant?.hosts?.[0]) {
      const host = tenant.hosts[0];
      // Ensure protocol is included (default to https)
      if (host.startsWith('http://') || host.startsWith('https://')) {
        return host;
      }
      return `https://${host}`;
    }
  }

  // Fallback to default origin (only used for URL parsing, not in final output)
  return 'https://www.zoocasa.com';
}

export const getInsightsHost = (isServerSide: boolean) => isServerSide ? configJSON.services.insights.serverSideHost : configJSON.services.insights.clientSideHost;

export const getGoSearchHost = (isServerSide: boolean) => isServerSide ? configJSON.services.search.serverSideHost : configJSON.services.search.clientSideHost;

export const getClientAppHost = (isServerSide: boolean) => isServerSide ? configJSON.services.clientApp.serverSideHost : configJSON.services.clientApp.clientSideHost;

export const getCmsHost = () => configJSON.services.cms;

export const getWalkScoreHost = () => configJSON.services.walkScore;

export const getProviderComplianceHost = () => configJSON.providerTermsOfUseHost;

/**
 * Get the storage service host for the given tenant.
 *
 * @param isServerSideHost Whether the host is for the server side or client side. Defaults to the value of {@link isServerSide isServerSide()}.
 * @returns The storage service host.
 */
export const getStorageServiceHost = (isServerSideHost: boolean = isServerSide()) => isServerSideHost ? configJSON.services.storage.serverSideHost : configJSON.services.storage.clientSideHost;

/**
 * Get the admin app host that owns the neighbourhood-images generation path.
 *
 * The server-side host is an internal cluster address that bypasses the public
 * edge's HTTP Basic Auth, so this should almost always be called with
 * `isServerSideHost = true` (the generation proxy runs in a Next.js API route).
 *
 * @param isServerSideHost Whether to return the internal (server) host or the public (client) host. Defaults to {@link isServerSide isServerSide()}.
 * @returns The admin app host.
 */
export const getNeighbourhoodImagesAdminHost = (isServerSideHost: boolean = isServerSide()) => isServerSideHost ? configJSON.services.neighbourhoodImagesAdmin.serverSideHost : configJSON.services.neighbourhoodImagesAdmin.clientSideHost;

export const getTagForgeHost = (isServerSideHost: boolean = isServerSide()) => isServerSideHost ? configJSON.services.tagforge.serverSideHost : configJSON.services.tagforge.clientSideHost;